import Flex from 'components/ui/Flex';
import Button from 'components/ui/Button';
import Text from 'components/ui/Text';
import Spinner from 'components/ui/Spinner';
import { useTranslation } from 'react-i18next';
import Modal from 'components/ui/Modal/Modal';
import { styled } from 'styled-components';
import { ReturnCenterDisplaySettingsResponse, ReturnOrderHeader } from 'returnCenter-api';
import Input from 'components/ui/Input';
import Table, { TableRow } from 'components/ui/Table';
import ItemCountDisplay from 'components/ui/ItemCountDisplay';
import BarcodeSearchModeContent, { BarcodeAddedOrder } from './BarcodeSearchModeContent';
import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcode, faExclamationTriangle, faTag, faUser } from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import ErrorDisplay from 'components/Error/ErrorDisplay';
import useAppDispatch from 'hooks/useAppDispatch';
import { ActionCreatorWithoutPayload } from '@reduxjs/toolkit';
import useAppSelector from 'hooks/useAppSelector';
import { companiesSelector } from 'store/reducers/companiesSlice';
import OrderDateFilter from 'components/Filters/OrderDateFilter';

export type SearchMode = 'orderNumber' | 'articleNumber' | 'barcode';

type SearchModalProps = {
    tableCols: string[];
    rows: () => TableRow[];
    setIsModalOpen: (isOpen: boolean) => void;
    clearSearchOrders: ActionCreatorWithoutPayload<'order/clearSearchOrders'>;
    setSearchQuery: (query: string) => void;
    searchQuery: string;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    loadingOrders?: boolean;
    errorMessage: string | null;
    searchResults: ReturnOrderHeader[];
    handleSearch: () => void;
    /** Enable order/article search mode toggle */
    showSearchModeToggle?: boolean;
    onSearchModeChange?: (mode: SearchMode) => void;
    statusMessage?: string | null;
    statusIcon?: IconDefinition | null;
    barcodeLookupLoading?: boolean;
    barcodeAddedOrders?: BarcodeAddedOrder[];
    isBarcodeSearchMode?: boolean;
    isArticleSearchMode?: boolean;
    isOrderNumberSearchMode?: boolean;
};

const SearchModal = ({
    rows,
    tableCols,
    setIsModalOpen,
    setSearchQuery,
    clearSearchOrders,
    searchQuery,
    handleInputChange,
    handleSearch,
    loadingOrders,
    errorMessage,
    searchResults,
    showSearchModeToggle,
    onSearchModeChange,
    statusMessage,
    statusIcon,
    barcodeLookupLoading,
    barcodeAddedOrders,
    isBarcodeSearchMode,
    isArticleSearchMode,
    isOrderNumberSearchMode,
}: SearchModalProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const inputRef = useRef<HTMLInputElement>(null);
    const siteSettings = useAppSelector(companiesSelector.selectDisplaySettings);

    const searchInputPlaceholder = isArticleSearchMode
        ? t('searchByArticlePlaceholder')
        : isBarcodeSearchMode
        ? t('scanOrEnterBarcode', 'Scan or enter barcode...')
        : t('searchForOrder');

    const minimumSearchLength = isArticleSearchMode ? 3 : isBarcodeSearchMode ? 8 : 2;
    const hasValidSearchInputLength = searchQuery.trim().length >= minimumSearchLength;
    const hasBarcodeAddedOrders = (barcodeAddedOrders?.length ?? 0) > 0;
    const emptyStateText = statusMessage
        ? statusMessage
        : hasValidSearchInputLength
        ? t('noOrdersAvailable', 'No orders found')
        : searchInputPlaceholder;

    const shouldShowGlobalLoading = !!loadingOrders;
    // Barcode mode uses inline status feedback instead of the global error block.
    const shouldShowGlobalError = !isBarcodeSearchMode && !!errorMessage;
    const shouldShowStandardModeEmptyState = !isBarcodeSearchMode && searchResults.length === 0;

    const renderBarcodeModeContent = () => {
        if (!isBarcodeSearchMode) return null;

        return (
            <BarcodeSearchModeContent
                statusMessage={statusMessage}
                statusIcon={statusIcon}
                emptyStateText={emptyStateText}
                barcodeAddedOrders={barcodeAddedOrders}
                isLoading={barcodeLookupLoading}
            />
        );
    };

    const renderStandardModeContent = () => {
        if (isBarcodeSearchMode) return null;

        if (shouldShowStandardModeEmptyState) {
            return (
                <Flex column align="center">
                    {statusMessage && statusIcon && <FontAwesomeIcon className="mt-3" icon={statusIcon} size="2x" />}
                    <Text className="mt-3" fontSize={20} thin as="label">
                        {emptyStateText}
                    </Text>
                </Flex>
            );
        }

        return (
            <TableWrapper column gap="1rem" $grow={1} className="orders-table-wrapper">
                <ItemCountDisplay title={t('orders')} count={searchResults.length} countLabel={t('items')} />
                <Table
                    className="orders-table f3-300"
                    bgColor="fillMedium"
                    cols={tableCols.length}
                    headers={tableCols}
                    rows={rows()}
                    tintOddRows
                />
            </TableWrapper>
        );
    };

    // Focus the input when the modal opens and search mode is barcode
    useEffect(() => {
        if (!isBarcodeSearchMode) return;

        const raf = requestAnimationFrame(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        });

        return () => cancelAnimationFrame(raf);
    }, [isBarcodeSearchMode]);

    return (
        <Modal tint show blur>
            <ModalContent column data-allow-paste>
                <TopSection justify="between" align="center">
                    <Text margin={0} as="h2" fontSize={20}>
                        {t('searchOrders')}
                    </Text>
                    <Button
                        className="ml-2"
                        onClick={() => {
                            setIsModalOpen(false);
                            dispatch(clearSearchOrders());
                            setSearchQuery('');
                        }}
                        color="text"
                        bgColor="fillWhite"
                        border
                        rounded
                    >
                        {t('close')}
                    </Button>
                </TopSection>
                {showSearchModeToggle && onSearchModeChange && (
                    <ModeButtons align="center" gap="1rem">
                        <ModeButton
                            $active={isOrderNumberSearchMode}
                            $siteSettings={siteSettings}
                            onClick={() => onSearchModeChange('orderNumber')}
                        >
                            <FontAwesomeIcon icon={faUser} />
                            <span>{t('searchByOrderNumber')}</span>
                        </ModeButton>
                        <ModeButton
                            $active={isArticleSearchMode}
                            $siteSettings={siteSettings}
                            onClick={() => onSearchModeChange('articleNumber')}
                        >
                            <FontAwesomeIcon icon={faTag} />
                            <span>{t('searchByArticle')}</span>
                        </ModeButton>
                        <ModeButton
                            $active={isBarcodeSearchMode}
                            $siteSettings={siteSettings}
                            onClick={() => onSearchModeChange('barcode')}
                        >
                            <FontAwesomeIcon icon={faBarcode} />
                            <span>{t('scanBarcodes', 'Scan barcode')}</span>
                        </ModeButton>
                    </ModeButtons>
                )}

                <Flex className="mt-2 mb-2" gap="0.5rem">
                    <StyledInput
                        id="table-input"
                        ref={inputRef}
                        className="table-input"
                        type="text"
                        placeholder={searchInputPlaceholder}
                        value={searchQuery}
                        loading={isBarcodeSearchMode ? barcodeLookupLoading : loadingOrders}
                        rightSidedIcon
                        onChange={handleInputChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSearch();
                            }
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                setIsModalOpen(false);
                                dispatch(clearSearchOrders());
                                setSearchQuery('');
                            }
                        }}
                        onFocus={(e) => e.target.select()}
                        onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData('text');
                            setSearchQuery(pastedText);
                        }}
                    />

                    {!isBarcodeSearchMode && <OrderDateFilter />}
                </Flex>

                <Container
                    align="center"
                    gap="1rem"
                    column
                    $grow={1}
                    data-has-barcode-added-orders={hasBarcodeAddedOrders}
                >
                    {shouldShowGlobalLoading ? (
                        <Flex className="mt-3" column $grow={1} gap="1rem" align="center">
                            <Spinner className="mt-3" useFullHeight center size="small" color="text" />
                        </Flex>
                    ) : shouldShowGlobalError ? (
                        <ErrorDisplay
                            errorMessage={errorMessage}
                            title={t('error_occurred')}
                            icon={faExclamationTriangle}
                        />
                    ) : (
                        <>
                            {renderBarcodeModeContent()}
                            {renderStandardModeContent()}
                        </>
                    )}
                </Container>
            </ModalContent>
        </Modal>
    );
};

export default SearchModal;

const TopSection = styled(Flex)`
    padding-top: 1rem;
`;

const ModeButtons = styled(Flex)`
    margin-top: 0.25rem;
`;

const ModeButton = styled.span<{ $active?: boolean; $siteSettings?: ReturnCenterDisplaySettingsResponse | null }>`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding-bottom: 0.35rem;
    font-size: 14px;
    font-weight: ${({ $active }) => ($active ? 600 : 400)};
    color: ${({ $active, theme, $siteSettings }) =>
        $active ? $siteSettings?.accentThemeColor ?? theme.colors.accent : theme.colors.textFaded};
    border-bottom: 2px solid
        ${({ $active, theme, $siteSettings }) =>
            $active ? $siteSettings?.accentThemeColor ?? theme.colors.accent : 'transparent'};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        color: ${({ $active, theme, $siteSettings }) =>
            !$active ? $siteSettings?.accentThemeColor ?? theme.colors.accent : undefined};
    }
`;

const ModalContent = styled(Flex)`
    padding: 0 1rem 1rem 1rem !important;
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 70%;
    height: 60vh;
`;

const Container = styled(Flex)`
    overflow: auto;
`;

const TableWrapper = styled(Flex)`
    width: 100%;
`;

const StyledInput = styled(Input)<{ underline?: boolean }>`
    width: 100%;
    flex: 1;
    input {
        width: 100%;
        border: 1px solid ${({ theme }) => theme.colors.border} !important;
        border-radius: var(--radius);
        font-size: 15px !important;
        letter-spacing: 0.02em !important;
        font-weight: 500 !important;
        height: 3rem !important;
    }
`;
